import {
  telemetryInternalIds,
  telemetryIdsFromUrl,
} from './telemetry-identifiers';
import type { SpanJSON, TransactionEvent } from '@sentry/core';
import { filterSentryError, sentryRoute } from './sentry-data-policy';

export const SENTRY_TRACE_PROBE_NAME = 'Sentry verification: trace filtering';
const operations = new Set([
  'pageload',
  'navigation',
  'ui.angular.routing',
  'http.client',
  'resource.script',
  'resource.css',
  'resource.link',
  'resource.img',
  'resource.fetch',
  'resource.other',
  'resource.xmlhttprequest',
  'ui.click',
  'ui.long-task',
  'ui.long-animation-frame',
  'app.operation',
]);
const statuses = new Set([
  'ok',
  'cancelled',
  'unknown_error',
  'invalid_argument',
  'deadline_exceeded',
  'not_found',
  'already_exists',
  'permission_denied',
  'resource_exhausted',
  'failed_precondition',
  'aborted',
  'out_of_range',
  'unimplemented',
  'internal_error',
  'unavailable',
  'data_loss',
  'unauthenticated',
]);
const origins = new Set([
  'manual',
  'auto.http.browser',
  'auto.ui.browser',
  'auto.ui.angular',
  'auto.pageload.browser',
  'auto.navigation.browser',
  'auto.resource.browser',
]);

function operation(value: unknown): string {
  return typeof value === 'string' && operations.has(value)
    ? value
    : 'app.operation';
}
function hex(value: unknown, length: number): string | undefined {
  return typeof value === 'string' &&
    new RegExp(`^[\\da-f]{${length}}$`, 'i').test(value)
    ? value
    : undefined;
}
function number(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isFinite(value) && value >= 0
    ? value
    : undefined;
}
function httpMethod(value: unknown): string | undefined {
  return typeof value === 'string' &&
    /^(GET|POST|PUT|PATCH|DELETE|HEAD|OPTIONS)$/i.test(value)
    ? value.toUpperCase()
    : undefined;
}
function httpStatus(value: unknown): number | undefined {
  return typeof value === 'number' &&
    Number.isInteger(value) &&
    value >= 0 &&
    value <= 599
    ? value
    : undefined;
}

/** Do not copy arbitrary span attributes, even if their values look numeric. */
function attributes(input: SpanJSON['data']): SpanJSON['data'] {
  const output: SpanJSON['data'] = {
    ...telemetryIdsFromUrl(
      input['url.full'] ?? input['http.url'] ?? input['url'],
    ),
    ...telemetryInternalIds(input),
  };
  const method = httpMethod(
    input['http.request.method'] ?? input['http.method'],
  );
  if (method) output['http.request.method'] = method;
  const status = httpStatus(
    input['http.response.status_code'] ?? input['http.status_code'],
  );
  if (status !== undefined) output['http.response.status_code'] = status;
  const url =
    input['url.full'] ?? input['http.url'] ?? input['url'] ?? input['url.path'];
  if (url !== undefined) output['url.path'] = sentryRoute(url);
  for (const key of ['http.response.body.size', 'http.request.body.size']) {
    const size = number(input[key]);
    if (size !== undefined) output[key] = size;
  }
  if (input['url.scheme'] === 'https' || input['url.scheme'] === 'http')
    output['url.scheme'] = input['url.scheme'];
  if (
    typeof input['sentry.source'] === 'string' &&
    ['route', 'custom', 'component'].includes(input['sentry.source'])
  )
    output['sentry.source'] = input['sentry.source'];
  if (
    typeof input['sentry.origin'] === 'string' &&
    origins.has(input['sentry.origin'])
  )
    output['sentry.origin'] = input['sentry.origin'];
  if (input['sentry.op'] !== undefined)
    output['sentry.op'] = operation(input['sentry.op']);
  const rate = number(input['sentry.sample_rate']);
  if (rate !== undefined && rate <= 1) output['sentry.sample_rate'] = rate;
  return output;
}

function name(value: unknown, op: string, data: SpanJSON['data']): string {
  if (value === SENTRY_TRACE_PROBE_NAME) return SENTRY_TRACE_PROBE_NAME;
  if (op === 'http.client') {
    const description = typeof value === 'string' ? value : '';
    const match = /^(GET|POST|PUT|PATCH|DELETE|HEAD|OPTIONS) (.+)$/i.exec(
      description,
    );
    const method =
      httpMethod(data['http.request.method']) ?? httpMethod(match?.[1]);
    const route = data['url.path'] ?? sentryRoute(match?.[2] ?? value);
    return `${method ?? 'HTTP'} ${route}`;
  }
  if (['navigation', 'pageload', 'ui.angular.routing'].includes(op)) {
    const route = sentryRoute(value);
    return route === '/unclassified' ? 'Angular navigation' : route;
  }
  return op.startsWith('resource.')
    ? 'Browser resource'
    : 'Application operation';
}

/** Public SDK child-span hook; transaction filtering repeats this at the boundary. */
export function filterSentrySpan(span: SpanJSON): SpanJSON {
  const data = attributes(span.data ?? {});
  const op = operation(span.op);
  if (op === 'http.client' && typeof span.description === 'string')
    for (const [key, value] of Object.entries(
      telemetryIdsFromUrl(
        span.description.replace(
          /^(?:GET|POST|PUT|PATCH|DELETE|HEAD|OPTIONS) /i,
          '',
        ),
      ),
    ))
      if (data[key] === undefined) data[key] = value;
  return {
    trace_id: hex(span.trace_id, 32) ?? '00000000000000000000000000000000',
    span_id: hex(span.span_id, 16) ?? '0000000000000000',
    parent_span_id: hex(span.parent_span_id, 16),
    start_timestamp: span.start_timestamp,
    timestamp: span.timestamp,
    exclusive_time: number(span.exclusive_time),
    op,
    description: name(span.description, op, data),
    data,
    status: span.status && statuses.has(span.status) ? span.status : undefined,
    origin: span.origin && origins.has(span.origin) ? span.origin : undefined,
    is_segment: span.is_segment,
    segment_id: hex(span.segment_id, 16),
  };
}

/** Performance measurements have their own schema; arbitrary business values are excluded. */
function measurements(
  input: TransactionEvent['measurements'],
): TransactionEvent['measurements'] {
  if (!input) return undefined;
  const output: NonNullable<TransactionEvent['measurements']> = {};
  for (const key of ['fp', 'fcp', 'lcp', 'cls', 'inp', 'ttfb']) {
    const measurement = input[key];
    if (!measurement || number(measurement.value) === undefined) continue;
    if (
      measurement.unit !== undefined &&
      measurement.unit !== 'millisecond' &&
      measurement.unit !== 'none'
    )
      continue;
    output[key] = { value: measurement.value, unit: measurement.unit };
  }
  return output;
}

/** Traces need their own final hook: beforeSend only processes error events. */
export function filterSentryTransaction(
  event: TransactionEvent,
): TransactionEvent | null {
  const trace = event.contexts?.trace;
  if (!trace || !hex(trace.trace_id, 32) || !hex(trace.span_id, 16))
    return null;
  const shared = filterSentryError({ ...event, type: undefined });
  const op = operation(trace.op);
  const data = attributes(trace.data ?? {});
  const transaction = name(event.transaction, op, data);
  const tags = { ...shared.tags };
  if (
    event.tags?.['source'] === 'sentry_trace_test' &&
    typeof event.tags['test_run_id'] === 'string' &&
    /^[\da-f]{8}-[\da-f]{4}-[\da-f]{4}-[\da-f]{4}-[\da-f]{12}$/i.test(
      event.tags['test_run_id'],
    )
  ) {
    tags['source'] = 'sentry_trace_test';
    tags['test_run_id'] = event.tags['test_run_id'];
    tags['test_scenario'] = 'trace_filtering';
  }
  if (shared.sdkProcessingMetadata?.['dynamicSamplingContext'])
    shared.sdkProcessingMetadata['dynamicSamplingContext'] = {
      ...shared.sdkProcessingMetadata['dynamicSamplingContext'],
      transaction,
    };
  return {
    type: 'transaction',
    event_id: event.event_id,
    platform: event.platform,
    timestamp: event.timestamp,
    start_timestamp: event.start_timestamp,
    release: shared.release,
    environment: shared.environment,
    dist: shared.dist,
    sdk: shared.sdk,
    user: shared.user,
    tags,
    extra: shared.extra,
    transaction,
    transaction_info: {
      source: transaction.startsWith('/') ? 'route' : 'custom',
    },
    contexts: {
      trace: {
        trace_id: trace.trace_id,
        span_id: trace.span_id,
        parent_span_id: hex(trace.parent_span_id, 16),
        op,
        data,
        status:
          typeof trace.status === 'string' && statuses.has(trace.status)
            ? trace.status
            : undefined,
      },
    },
    spans: event.spans?.map(filterSentrySpan),
    measurements: measurements(event.measurements),
    breadcrumbs: shared.breadcrumbs,
    sdkProcessingMetadata: shared.sdkProcessingMetadata,
  };
}
